import moment from "moment";
import { functions, httpsCallable } from "@/firebase/init";
import { fetchIntegrationCredential } from "@/utils/integrationCredentialCallable.js";
import registry from "../../shared/integrationSecretRegistry.js";

// Registry — edit shared/integrationSecretRegistry.js (also used by Cloud Functions).
export const INTEGRATION_SECRET_SLOTS = registry.INTEGRATION_SECRET_SLOTS;
export const ALLOWED_FIELD_KEYS = registry.ALLOWED_FIELD_KEYS;
export const PAYABLE_TO_SCOPED_PLATFORMS = registry.PAYABLE_TO_SCOPED_PLATFORMS;
export const PLATFORMS_WITH_GSM_INTEGRATION_SECRETS =
  registry.PLATFORMS_WITH_GSM_INTEGRATION_SECRETS;
export const getSlot = registry.getSlot;
export const platformFromFieldKey = registry.platformFromFieldKey;
export const isPayableToScopedFieldKey = registry.isPayableToScopedFieldKey;
export const payableToScopedFieldKey = registry.payableToScopedFieldKey;
export const peekLegacySecretOnIntegrationDoc =
  registry.peekLegacySecretOnIntegrationDoc;
export const deriveLegacyFirestoreSecretSignals =
  registry.deriveLegacyFirestoreSecretSignals;
export const sanitizeIntegrationDocSecretsForViewer =
  registry.sanitizeIntegrationDocSecretsForViewer;
export const sanitizeDraftPlatformSecrets = registry.sanitizeDraftPlatformSecrets;
export const getTenantFieldKeysForPlatform = registry.getTenantFieldKeysForPlatform;
export const getLegacySecretFromDoc = registry.getLegacySecretFromDoc;

// --- Admin UI helpers (handles, timestamps, store callable) ---

export function integrationCredentialHandle(handles, fieldKey) {
  return handles?.[fieldKey];
}

export function credentialHasSecretManagerMeta(handles, fieldKey) {
  const handle = integrationCredentialHandle(handles, fieldKey);
  return !!(handle && (handle.secretId || handle.latestVersionResource));
}

export function formatIntegrationHandleTimestamp(ts) {
  if (!ts) {
    return "";
  }
  let millis = null;
  if (typeof ts.toMillis === "function") {
    millis = ts.toMillis();
  } else if (typeof ts.toDate === "function") {
    millis = ts.toDate().getTime();
  } else if (typeof ts.seconds === "number") {
    millis = ts.seconds * 1000;
  } else if (ts instanceof Date) {
    millis = ts.getTime();
  }
  if (millis == null) {
    return "";
  }
  return moment(Math.floor(millis)).format("MM/DD/YYYY @ hh:mm A");
}

export function secretHandleUpdatedLabel(handleLike) {
  if (!handleLike?.updatedAt) {
    return "";
  }
  return formatIntegrationHandleTimestamp(handleLike.updatedAt);
}

export function mergeSecretHandle(handles, fieldKey, handleMeta) {
  const prevHandles =
    handles && typeof handles === "object" ? handles : {};
  return {
    ...prevHandles,
    [fieldKey]: handleMeta,
  };
}

/**
 * @param {{ fieldKey: string, secretValue: string, payableToId?: string }} options
 * @returns {Promise<{ secretId: string, latestVersionResource: string, updatedAt: Date }>}
 */
export async function storeIntegrationSecretViaCallable({
  fieldKey,
  secretValue,
  payableToId,
}) {
  const storeSecret = httpsCallable(functions, "storeIntegrationSecret");
  const payload = { fieldKey, secretValue };
  if (payableToId) {
    payload.payableToId = payableToId;
  }
  const callableResult = await storeSecret(payload);
  const data = callableResult?.data;
  if (!data?.secretId) {
    throw new Error("Secret storage did not return metadata.");
  }
  return {
    secretId: data.secretId,
    latestVersionResource: data.versionName,
    updatedAt: new Date(),
  };
}

// --- PayableTo runtime hydration ---

export function isPayableToScopedPlatform(platform) {
  return PAYABLE_TO_SCOPED_PLATFORMS.includes(platform);
}

/**
 * Resolves GSM API keys onto payableTo.integrations.*.api for in-memory use only.
 *
 * @param {{ id: string, integrations?: object }} payableTo
 * @param {string[]} [platforms]
 * @returns {Promise<typeof payableTo>}
 */
export async function hydratePayableToIntegrationSecrets(
  payableTo,
  platforms = PAYABLE_TO_SCOPED_PLATFORMS,
) {
  if (!payableTo?.id || !payableTo.integrations) {
    return payableTo;
  }

  await Promise.all(
    platforms.map(async (platform) => {
      const block = payableTo.integrations[platform];
      if (!block) {
        return;
      }
      try {
        const api = await fetchIntegrationCredential(
          payableToScopedFieldKey(platform),
          { payableToId: payableTo.id },
        );
        payableTo.integrations[platform] = { ...block, api };
      } catch (err) {
        console.error(`hydratePayableToIntegrationSecrets(${platform})`, err);
      }
    }),
  );

  return payableTo;
}

const LEAFLINK_API_KEY_ERROR =
  "LeafLink API key is not configured for this Payable To. Check Admin → Integrations.";

/**
 * GSM-first LeafLink API key onto payableTo.integrations.leaflink.api (in-memory).
 *
 * @param {{ id: string, integrations?: { leaflink?: object } }} payableTo
 * @returns {Promise<string>}
 */
export async function resolveLeaflinkApiKey(payableTo) {
  if (!payableTo?.id) {
    throw new Error("resolveLeaflinkApiKey: payableTo.id required");
  }
  await hydratePayableToIntegrationSecrets(payableTo, ["leaflink"]);
  const api = payableTo?.integrations?.leaflink?.api?.trim();
  if (!api) {
    throw new Error(LEAFLINK_API_KEY_ERROR);
  }
  return api;
}

/**
 * Like resolveLeaflinkApiKey, but shows a toast and returns null instead of throwing.
 * Use in Vue methods; keep resolveLeaflinkApiKey for non-UI code that should fail fast.
 *
 * @param {object} component Vue component instance (for toast)
 * @param {{ id: string, integrations?: { leaflink?: object } }} payableTo
 * @param {{ errorTitle?: string }} [options]
 * @returns {Promise<string|null>}
 */
export async function resolveLeaflinkApiKeyForUi(
  component,
  payableTo,
  { errorTitle = "Could not load LeafLink API key" } = {},
) {
  try {
    return await resolveLeaflinkApiKey(payableTo);
  } catch (err) {
    console.error("resolveLeaflinkApiKeyForUi", err);
    component?.$root?.toast?.show({
      title: errorTitle,
      subtitle: err?.message || LEAFLINK_API_KEY_ERROR,
      color: "error",
    });
    return null;
  }
}

/**
 * @param {object} payableToData
 * @param {string} platform
 * @returns {string}
 */
export function peekPayableToLegacyApi(payableToData, platform) {
  const legacy = getLegacySecretFromDoc(
    payableToData,
    payableToScopedFieldKey(platform),
  );
  return legacy || "";
}

/**
 * Resolves a payableTo-scoped API key: unsaved draft input, then GSM with
 * server-side legacy Firestore fallback (same order as integrationSecretResolver).
 *
 * @param {object} payableToData Payable To doc (needs `id` for GSM lookup).
 * @param {string} platform e.g. "leaflink", "flourish", "apextrading"
 * @param {{ draftApi?: string }} [options]
 * @returns {Promise<string>}
 */
export async function resolvePayableToScopedApiKey(
  payableToData,
  platform,
  { draftApi = "" } = {},
) {
  const draft = String(draftApi || "").trim();
  if (draft) {
    return draft;
  }

  if (!payableToData?.id) {
    throw new Error("resolvePayableToScopedApiKey: payableTo id required");
  }

  return fetchIntegrationCredential(payableToScopedFieldKey(platform), {
    payableToId: payableToData.id,
  });
}
